import type { ReactNode } from "react";
import type { RaRecord } from "react-admin";
import {
  useShowContext,
  ReferenceManyField,
  useListContext,
  ReferenceField,
  TextField,
} from "react-admin";
import {
  Paper,
  Typography,
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import BalanceSummary from "./BalanceSummary";
import { formatListDate, formatDisplayDateTime } from "@/utils/date-format";
import { isMuttavatteenSabilType } from "../sabil-household-members";
import {
  resolveHolderWhatsapp,
  resolveSabilDisplayItsNo,
  resolveSabilDisplayMohallah,
  resolveSabilDisplayName,
} from "../sabil-show-identity";
import {
  formatSabilLastPaidDate,
  formatTakhmeenStartDate,
  isEstablishmentOrProfessionalSabilType,
  resolveSabilStatusLabel,
} from "../sabil-show-display";

const InfoSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <Paper
    elevation={1}
    sx={{
      p: 2,
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <Typography
      variant="h6"
      sx={{
        mb: 1.5,
        pb: 0.75,
        borderBottom: "2px solid",
        borderColor: "primary.main",
        fontWeight: 600,
        color: "text.primary",
        fontSize: "1.1rem",
      }}
    >
      {title}
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>{children}</Box>
  </Paper>
);

const getByPath = (record: RaRecord | undefined, source: string): unknown => {
  if (!record) return undefined;
  return source.split(".").reduce<unknown>((obj, key) => {
    if (obj && typeof obj === "object" && key in obj) {
      return (obj as Record<string, unknown>)[key];
    }
    return undefined;
  }, record);
};

const InfoField = ({
  source,
  label,
  record,
}: {
  source: string;
  label?: string;
  record?: RaRecord;
}) => {
  const value = getByPath(record, source);

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600, fontSize: "0.75rem" }}
      >
        {label || source}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25 }}>
        {value != null && value !== "" ? String(value) : "—"}
      </Typography>
    </Box>
  );
};

type TakhmeenRow = RaRecord & {
  takhmeenAmount?: number;
  startDate?: string;
  updatedBy?: string;
};

const TakhmeenHistoryTable = () => {
  const { data, isLoading } = useListContext<TakhmeenRow>();

  if (isLoading) {
    return (
      <Typography variant="body2" sx={{ py: 2, color: "text.secondary" }}>
        Loading...
      </Typography>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Typography variant="body2" sx={{ py: 2, color: "text.secondary", textAlign: "center" }}>
        No takhmeen history available
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Updated By</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((takhmeen) => (
            <TableRow key={takhmeen.id}>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  ₹{(takhmeen.takhmeenAmount || 0).toLocaleString("en-IN")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatListDate(takhmeen.startDate, { empty: "—" })}
                </Typography>
              </TableCell>
              <TableCell>
                {takhmeen.updatedBy ? (
                  <ReferenceField
                    record={takhmeen}
                    source="updatedBy"
                    reference="admins"
                    link={false}
                  >
                    <TextField source="name" />
                  </ReferenceField>
                ) : (
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    —
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const BasicInfo = () => {
  const { record } = useShowContext<RaRecord>();
  const sabilType = record?.sabilType as string | undefined;
  const isMuttavatteen = isMuttavatteenSabilType(sabilType);
  const isEstOrProf = isEstablishmentOrProfessionalSabilType(sabilType);
  const displayName = resolveSabilDisplayName(record);
  const displayItsNo = resolveSabilDisplayItsNo(record);
  const displayMohallah = resolveSabilDisplayMohallah(record);
  const holderWhatsapp = resolveHolderWhatsapp(record);
  const statusLabel = resolveSabilStatusLabel(record?.closedAt);
  const isClosed = statusLabel === "Closed";

  return (
    <Box sx={{ p: 1 }}>
      <BalanceSummary />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <InfoSection title="Sabil Information">
            <InfoField source="sabilNo" label="Sabil Number" record={record} />
            <Divider sx={{ my: 0.75 }} />
            <InfoField source="sabilType" label="Sabil Type" record={record} />
            <Divider sx={{ my: 0.75 }} />
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              >
                Status
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={statusLabel}
                  color={isClosed ? "default" : "success"}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>
            {isClosed ? (
              <>
                <Divider sx={{ my: 0.75 }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  >
                    Closed On
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25 }}>
                    {formatDisplayDateTime(record?.closedAt, { empty: "—" })}
                  </Typography>
                </Box>
                {record?.transferTo ? (
                  <>
                    <Divider sx={{ my: 0.75 }} />
                    <InfoField source="transferTo" label="Transfer To" record={record} />
                  </>
                ) : null}
                {record?.prevSabilNo ? (
                  <>
                    <Divider sx={{ my: 0.75 }} />
                    <InfoField source="prevSabilNo" label="Previous Sabil No." record={record} />
                  </>
                ) : null}
              </>
            ) : null}
            <Divider sx={{ my: 0.75 }} />
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              >
                Last Paid Date
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {formatSabilLastPaidDate(record?.lastPaidDate, sabilType)}
              </Typography>
            </Box>
            <Divider sx={{ my: 0.75 }} />
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              >
                Takhmeen Start Date
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {formatTakhmeenStartDate(record)}
              </Typography>
            </Box>
          </InfoSection>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <InfoSection title="Personal Information">
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              >
                Name
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {displayName ?? "—"}
              </Typography>
            </Box>
            <Divider sx={{ my: 0.75 }} />
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              >
                ITS Number
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {displayItsNo ?? "—"}
              </Typography>
            </Box>
            {isMuttavatteen ? (
              <>
                <Divider sx={{ my: 0.75 }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  >
                    WhatsApp
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25 }}>
                    {holderWhatsapp ?? "—"}
                  </Typography>
                </Box>
              </>
            ) : null}
            <Divider sx={{ my: 0.75 }} />
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              >
                Mohalla
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {displayMohallah ?? "—"}
              </Typography>
            </Box>
            <Divider sx={{ my: 0.75 }} />
            <InfoField source="address" label="Address" record={record} />
            {isEstOrProf ? (
              <>
                <Divider sx={{ my: 0.75 }} />
                <InfoField source="currentTakhmeenGrade" label="Grade" record={record} />
                <Divider sx={{ my: 0.75 }} />
                <InfoField source="firmName" label="Firm Name" record={record} />
                <Divider sx={{ my: 0.75 }} />
                <InfoField source="firmAddress" label="Firm Address" record={record} />
              </>
            ) : null}
          </InfoSection>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <InfoSection title="Takhmeen Information">
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              >
                Current Takhmeen Amount
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mt: 0.5,
                  fontWeight: 700,
                  color: "primary.main",
                }}
              >
                ₹
                {record?.sabilTakhmeenCurrent?.takhmeenAmount != null
                  ? Number(record.sabilTakhmeenCurrent.takhmeenAmount).toLocaleString("en-IN")
                  : "—"}
              </Typography>
            </Box>
          </InfoSection>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={12}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 1.5,
                pb: 0.75,
                borderBottom: "2px solid",
                borderColor: "primary.main",
                fontWeight: 600,
                color: "text.primary",
                fontSize: "1.1rem",
              }}
            >
              Takhmeen History
            </Typography>
            <ReferenceManyField
              reference="sabilTakhmeen"
              target="sabilId"
              label={false}
              sort={{ field: "startDate", order: "DESC" }}
              filter={{ sabilId: record?.id }}
            >
              <TakhmeenHistoryTable />
            </ReferenceManyField>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BasicInfo;
